import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { Add as AddIcon, Refresh as RefreshIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";

import KanbanEnterpriseBoard from "./KanbanEnterpriseBoard";
import BoardSelector from "./BoardSelector";
import ColumnEditor from "./ColumnEditor";
import ViewModeToggle from "./ViewModeToggle";
import BoardFilters from "./BoardFilters";
import DashboardMetrics from "./DashboardMetrics";
import {
  listBoards,
  createBoard,
  showBoard,
  updateBoard as updateBoardApi,
  deleteBoard as deleteBoardApi,
  createColumn as createColumnApi,
  updateColumn as updateColumnApi,
  deleteColumn as deleteColumnApi,
  reorderColumns as reorderColumnsApi,
  listColumnTickets,
  listUnassignedTickets,
  moveTicketToLane as moveTicketToLaneApi,
  quickUpdateTicket,
} from "../../services/kanbanEnterpriseService";
import UserSelectionDialog from "./UserSelectionDialog";
import QueueSelectionDialog from "./QueueSelectionDialog";
import ConnectionSelectionDialog from "./ConnectionSelectionDialog";
import TicketAuditDialog from "./TicketAuditDialog";
import {
  UNASSIGNED_COLUMN_ID,
  boardHasManualColumns,
  buildUnassignedColumn,
} from "./columnMembership";

const SOCKET_DEBOUNCE_MS = 2000;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    overflow: "hidden",
    borderRadius: 10,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0.5),
    flexShrink: 0,
  },
  boardArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    overflow: "hidden",
  },
  loadingCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    gap: theme.spacing(1.5),
  },
}));

const KanbanEnterprise = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  // ── State ──
  const [boards, setBoards] = useState([]);
  const [currentBoardId, setCurrentBoardId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [columnTickets, setColumnTickets] = useState({});
  const [columnCounts, setColumnCounts] = useState({});
  const [columnHasMore, setColumnHasMore] = useState({});
  const [columnLoading, setColumnLoading] = useState({});
  const [columnPages, setColumnPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("kanbanEnterprise_viewMode");
      if (saved && ["compact", "comfortable", "detailed"].includes(saved)) return saved;
    } catch (e) { /* ignore */ }
    return "comfortable";
  });
  const [cardStyle, setCardStyle] = useState(() => {
    try {
      const saved = localStorage.getItem("kanbanEnterprise_cardStyle");
      if (saved && ["rich", "discrete"].includes(saved)) return saved;
    } catch (e) { /* ignore */ }
    return "rich";
  });
  const [boardFilters, setBoardFilters] = useState({});

  // Quick-action selector dialogs
  const [selectorDialog, setSelectorDialog] = useState({
    type: null, // "assignee" | "queue" | "connection"
    ticketId: null,
  });
  const [auditTicketId, setAuditTicketId] = useState(null);

  const debounceRef = useRef(null);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const columnIdsMatch = useCallback(
    (a, b) => Number(a) === Number(b),
    []
  );

  const upsertColumn = useCallback((prev, column) => {
    if (!column?.id) return prev;
    const exists = prev.some((c) => columnIdsMatch(c.id, column.id));
    if (exists) {
      return prev.map((c) =>
        columnIdsMatch(c.id, column.id) ? { ...c, ...column } : c
      );
    }
    return [...prev, column].sort((a, b) => a.position - b.position);
  }, [columnIdsMatch]);

  // ── Client-side filtering ──
  const filteredColumnTickets = useMemo(() => {
    const f = boardFilters || {};
    const hasFilters =
      (f.userIds && f.userIds.length > 0) ||
      (f.queueIds && f.queueIds.length > 0) ||
      (f.priority && f.priority.length > 0) ||
      (f.walletUserIds && f.walletUserIds.length > 0) ||
      (f.searchText && f.searchText.trim());

    if (!hasFilters) return columnTickets;

    const result = {};
    for (const [colId, tickets] of Object.entries(columnTickets)) {
      result[colId] = (tickets || []).filter((t) => {
        if (f.userIds?.length && !f.userIds.includes(t.userId)) return false;
        if (f.queueIds?.length && !f.queueIds.includes(t.queueId)) return false;
        if (f.priority?.length && !f.priority.includes(t.priority || "normal")) return false;
        if (f.walletUserIds?.length) {
          const wallets = t.contact?.ContactWallets || [];
          const walletIds = wallets.map((w) => w.walletId || w.userId);
          if (!f.walletUserIds.some((id) => walletIds.includes(id))) return false;
        }
        if (f.searchText?.trim()) {
          const s = f.searchText.toLowerCase();
          const contactName = (t.contact?.name || "").toLowerCase();
          const contactNumber = (t.contact?.number || "").toLowerCase();
          const lastMsg = (t.lastMessage || "").toLowerCase();
          if (!contactName.includes(s) && !contactNumber.includes(s) && !lastMsg.includes(s)) return false;
        }
        return true;
      });
    }
    return result;
  }, [columnTickets, boardFilters]);

  // Filtered counts
  const filteredColumnCounts = useMemo(() => {
    const counts = {};
    for (const [colId, tickets] of Object.entries(filteredColumnTickets)) {
      counts[colId] = (tickets || []).length;
    }
    return counts;
  }, [filteredColumnTickets]);

  // ── Persist viewMode + cardStyle to localStorage ──
  useEffect(() => {
    try { localStorage.setItem("kanbanEnterprise_viewMode", viewMode); } catch (e) { /* ignore */ }
  }, [viewMode]);
  useEffect(() => {
    try { localStorage.setItem("kanbanEnterprise_cardStyle", cardStyle); } catch (e) { /* ignore */ }
  }, [cardStyle]);

  // ── Load boards on mount ──
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        setLoading(true);
        let boardList = await listBoards();

        // Se não tem nenhum board, criar default
        if (!boardList || boardList.length === 0) {
          const newBoard = await createBoard({
            name: "Meu Kanban",
            template: "status",
            boardType: "global",
          });
          boardList = [newBoard];
        }

        if (cancelled) return;
        setBoards(boardList);

        // Selecionar board: default ou primeiro
        const defaultBoard =
          boardList.find((b) => b.isDefault) || boardList[0];
        setCurrentBoardId(defaultBoard.id);
      } catch (err) {
        console.error("[KanbanEnterprise] init error:", err);
        toast.error("Erro ao carregar painéis");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Fetch tickets for a column ──
  const fetchColumnTickets = useCallback(
    async (column, page = 1, cancelled = false, boardIdOverride = null) => {
      const colId = column.id;
      const boardId = boardIdOverride || currentBoardId;
      setColumnLoading((prev) => ({ ...prev, [colId]: true }));
      try {
        const isInbox =
          column.isVirtual || String(colId) === UNASSIGNED_COLUMN_ID;

        const result = isInbox
          ? await listUnassignedTickets(boardId, page, 200)
          : await listColumnTickets(
              colId,
              column.filterJson || {},
              page,
              200
            );
        if (cancelled) return;

        setColumnTickets((prev) => ({
          ...prev,
          [colId]:
            page === 1
              ? result.tickets
              : [...(prev[colId] || []), ...result.tickets],
        }));
        setColumnCounts((prev) => ({ ...prev, [colId]: result.count }));
        setColumnHasMore((prev) => ({ ...prev, [colId]: result.hasMore }));
        setColumnPages((prev) => ({ ...prev, [colId]: page }));
      } catch (err) {
        console.error(`[KanbanEnterprise] fetch col ${colId} error:`, err);
      } finally {
        if (!cancelled) {
          setColumnLoading((prev) => ({ ...prev, [colId]: false }));
        }
      }
    },
    [currentBoardId]
  );

  // ── Load board details when currentBoardId changes ──
  useEffect(() => {
    if (!currentBoardId) return;
    let cancelled = false;

    const loadBoard = async () => {
      try {
        setLoading(true);
        const board = await showBoard(currentBoardId);
        if (cancelled) return;

        const cols = (board.columns || []).sort(
          (a, b) => a.position - b.position
        );
        setColumns(cols);
        setColumnTickets({});
        setColumnCounts({});
        setColumnHasMore({});
        setColumnPages({});

        // Fetch tickets for each column (+ inbox se houver colunas manuais)
        const fetchJobs = cols.map((col) => fetchColumnTickets(col, 1, cancelled, board.id));
        if (boardHasManualColumns(cols)) {
          fetchJobs.push(
            fetchColumnTickets(buildUnassignedColumn(), 1, cancelled, board.id)
          );
        }
        await Promise.all(fetchJobs);
      } catch (err) {
        console.error("[KanbanEnterprise] load board error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadBoard();
    return () => {
      cancelled = true;
    };
  }, [currentBoardId, fetchColumnTickets]);

  // ── Load more (infinite scroll) ──
  const handleLoadMore = useCallback(
    (colId) => {
      if (String(colId) === UNASSIGNED_COLUMN_ID) {
        const nextPage = (columnPages[colId] || 1) + 1;
        fetchColumnTickets(buildUnassignedColumn(), nextPage);
        return;
      }
      const col = columnsRef.current.find((c) => c.id === colId);
      if (!col) return;
      const nextPage = (columnPages[colId] || 1) + 1;
      fetchColumnTickets(col, nextPage);
    },
    [columnPages, fetchColumnTickets]
  );

  // ── Drag & Drop ──
  const handleDragEnd = useCallback(
    async (result) => {
      if (!result.destination) return;

      const { source, destination, draggableId, type } = result;

      // ── LANE drag (reorder colunas) ──
      if (type === "LANE") {
        if (source.index === destination.index) return;

        // Optimistic reorder
        setColumns((prev) => {
          const next = [...prev];
          const [moved] = next.splice(source.index, 1);
          next.splice(destination.index, 0, moved);
          return next;
        });

        // Persist
        try {
          const reorderedIds = (() => {
            const next = [...columnsRef.current];
            const [moved] = next.splice(source.index, 1);
            next.splice(destination.index, 0, moved);
            return next.map((c) => c.id);
          })();
          await reorderColumnsApi({
            boardId: currentBoardId,
            columnIds: reorderedIds,
          });
        } catch (err) {
          console.error("[KanbanEnterprise] Lane reorder error:", err);
          toast.error("Erro ao reordenar colunas");
          // Reload board to revert
          if (currentBoardId) {
            const board = await showBoard(currentBoardId);
            setColumns(
              (board.columns || []).sort((a, b) => a.position - b.position)
            );
          }
        }
        return;
      }

      // ── CARD drag ──
      const ticketId = Number(draggableId);

      // Mesma coluna, mesma posição
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      // Mesma coluna, reorder
      if (source.droppableId === destination.droppableId) {
        const colId = source.droppableId;
        setColumnTickets((prev) => {
          const tickets = [...(prev[colId] || [])];
          const [moved] = tickets.splice(source.index, 1);
          tickets.splice(destination.index, 0, moved);
          return { ...prev, [colId]: tickets };
        });
        return;
      }

      // Cross-column: resolve action
      const srcColId = source.droppableId;
      const dstColId = destination.droppableId;
      const dstIsInbox = String(dstColId) === UNASSIGNED_COLUMN_ID;
      const srcIsInbox = String(srcColId) === UNASSIGNED_COLUMN_ID;

      const dstCol = dstIsInbox
        ? buildUnassignedColumn()
        : columns.find((c) => String(c.id) === String(dstColId));
      const srcCol = srcIsInbox
        ? buildUnassignedColumn()
        : columns.find((c) => String(c.id) === String(srcColId));

      if (!dstCol) return;

      // Optimistic: move card between columns
      let movedTicket = null;
      setColumnTickets((prev) => {
        const srcTickets = [...(prev[srcColId] || [])];
        const dstTickets = [...(prev[dstColId] || [])];
        [movedTicket] = srcTickets.splice(source.index, 1);
        if (movedTicket) {
          dstTickets.splice(destination.index, 0, movedTicket);
        }
        return { ...prev, [srcColId]: srcTickets, [dstColId]: dstTickets };
      });
      setColumnCounts((prev) => ({
        ...prev,
        [srcColId]: Math.max(0, (prev[srcColId] || 0) - 1),
        [dstColId]: (prev[dstColId] || 0) + 1,
      }));

      // Resolve action based on destination column type/filter
      try {
        const updateData = {};

        if (!dstIsInbox) {
          const dstFilter = dstCol.filterJson || {};

          if (
            dstCol.type === "status" &&
            Array.isArray(dstFilter.status) &&
            dstFilter.status.length === 1
          ) {
            updateData.status = dstFilter.status[0];
          }

          if (
            dstCol.type === "queue" &&
            Array.isArray(dstFilter.queueIds) &&
            dstFilter.queueIds.length === 1
          ) {
            updateData.queueId = dstFilter.queueIds[0];
          }

          if (
            dstCol.type === "assignee" &&
            Array.isArray(dstFilter.userIds) &&
            dstFilter.userIds.length === 1
          ) {
            updateData.assigneeId = dstFilter.userIds[0];
          }
        }

        await moveTicketToLaneApi(ticketId, {
          columnId: dstIsInbox ? null : Number(dstColId),
          updateData,
        });

        // Reconciliar após move (ex.: closeOnEnter encerra e o filtro de status
        // não inclui "closed" — o card otimista precisa sumir).
        if (srcCol) fetchColumnTickets(srcCol, 1);
        if (dstCol) fetchColumnTickets(dstCol, 1);
      } catch (err) {
        console.error("[KanbanEnterprise] DnD action error:", err);
        toast.error("Erro ao mover ticket");
        // Revert: refetch both columns
        if (srcCol) fetchColumnTickets(srcCol, 1);
        if (dstCol) fetchColumnTickets(dstCol, 1);
      }
    },
    [columns, fetchColumnTickets, currentBoardId]
  );

  // ── Socket: realtime updates ──
  useEffect(() => {
    if (!companyId || !currentBoardId || !socket) return;

    const handleTicketUpdate = (data) => {
      if (!data || !["create", "update", "delete"].includes(data.action)) return;

      // Debounce: coalesce rapid socket events
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        // Refetch all columns (+ inbox se houver manuais)
        const cols = columnsRef.current;
        cols.forEach((col) => fetchColumnTickets(col, 1));
        if (boardHasManualColumns(cols)) {
          fetchColumnTickets(buildUnassignedColumn(), 1);
        }
      }, SOCKET_DEBOUNCE_MS);
    };

    // ── Kanban Enterprise events (column mutations from other users) ──
    const handleKanbanEvent = (data) => {
      if (!data || !data.action) return;

      switch (data.action) {
        case "column-update": {
          const updated = data.column;
          if (!updated) return;
          setColumns((prev) =>
            prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
          );
          break;
        }
        case "column-create": {
          const created = data.column;
          if (!created) return;
          setColumns((prev) => upsertColumn(prev, created));
          fetchColumnTickets(created, 1);
          break;
        }
        case "ticket-reappeared": {
          // Ticket reapareceu no Chat — destacar card no Kanban
          const { ticketId: reappearedId, columnId: reappearedCol } = data;
          if (!reappearedId) break;
          setColumnTickets((prev) => {
            const colKey = String(reappearedCol);
            if (!prev[colKey]) return prev;
            return {
              ...prev,
              [colKey]: prev[colKey].map((t) =>
                t.id === reappearedId
                  ? { ...t, kanbanHiddenFromChat: false }
                  : t
              ),
            };
          });
          break;
        }
        case "ticket-moved": {
          // Outro usuário moveu o lead — atualizar colunas do board atual
          if (
            data.boardId != null &&
            Number(data.boardId) !== Number(currentBoardId)
          ) {
            break;
          }
          const cols = columnsRef.current;
          cols.forEach((col) => fetchColumnTickets(col, 1));
          if (boardHasManualColumns(cols)) {
            fetchColumnTickets(buildUnassignedColumn(), 1);
          }
          break;
        }
        case "column-delete": {
          const deletedId = data.columnId;
          if (!deletedId) return;
          setColumns((prev) => prev.filter((c) => c.id !== deletedId));
          setColumnTickets((prev) => {
            const next = { ...prev };
            delete next[deletedId];
            return next;
          });
          break;
        }
        case "columns-reorder": {
          const reordered = data.columns;
          if (!Array.isArray(reordered)) return;
          setColumns(
            reordered.sort((a, b) => a.position - b.position)
          );
          break;
        }
        default:
          break;
      }
    };

    const ticketEvent = `company-${companyId}-ticket`;
    const messageEvent = `company-${companyId}-appMessage`;
    const kanbanEvent = `company-${companyId}-kanban-enterprise`;

    socket.on(ticketEvent, handleTicketUpdate);
    socket.on(messageEvent, handleTicketUpdate);
    socket.on(kanbanEvent, handleKanbanEvent);

    return () => {
      socket.off(ticketEvent, handleTicketUpdate);
      socket.off(messageEvent, handleTicketUpdate);
      socket.off(kanbanEvent, handleKanbanEvent);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [companyId, currentBoardId, fetchColumnTickets, socket, upsertColumn]);

  // ── Board CRUD handlers ──
  const handleSelectBoard = useCallback((boardId) => {
    setCurrentBoardId(boardId);
  }, []);

  const handleCreateBoard = useCallback(
    async ({ name, template, boardType = "global" }) => {
      try {
        const newBoard = await createBoard({ name, template, boardType });
        setBoards((prev) => [...prev, newBoard]);
        setCurrentBoardId(newBoard.id);
        toast.success("Painel criado!");
      } catch (err) {
        toast.error("Erro ao criar painel");
      }
    },
    []
  );

  const handleRenameBoard = useCallback(
    async (boardId, newName, options = {}) => {
      try {
        const payload = { name: newName };
        if (options.boardType) {
          payload.boardType = options.boardType;
          payload.isShared = options.boardType !== "personal";
        }
        const updated = await updateBoardApi(boardId, payload);
        setBoards((prev) =>
          prev.map((b) =>
            b.id === boardId
              ? {
                  ...b,
                  ...(updated || {}),
                  name: newName,
                  ...(options.boardType
                    ? {
                        boardType: options.boardType,
                        isShared: options.boardType !== "personal",
                      }
                    : {}),
                }
              : b
          )
        );
        toast.success("Painel atualizado!");
      } catch (err) {
        toast.error("Erro ao atualizar painel");
      }
    },
    []
  );

  const handleDeleteBoard = useCallback(
    async (boardId) => {
      try {
        await deleteBoardApi(boardId);
        setBoards((prev) => prev.filter((b) => b.id !== boardId));
        if (currentBoardId === boardId) {
          const remaining = boards.filter((b) => b.id !== boardId);
          setCurrentBoardId(remaining.length > 0 ? remaining[0].id : null);
        }
        toast.success("Painel excluído!");
      } catch (err) {
        toast.error("Erro ao excluir painel");
      }
    },
    [currentBoardId, boards]
  );

  // ── Column CRUD handlers ──
  const handleEditColumn = useCallback((column) => {
    setEditingColumn(column);
    setEditorOpen(true);
  }, []);

  const handleNewColumn = useCallback(() => {
    setEditingColumn(null);
    setEditorOpen(true);
  }, []);

  const handleSaveColumn = useCallback(
    async (colData) => {
      try {
        if (colData.id) {
          // Update
          const updated = await updateColumnApi(colData.id, {
            name: colData.name,
            type: colData.type,
            color: colData.color,
            icon: colData.icon,
            filterJson: colData.filterJson,
            settingsJson: colData.settingsJson,
          });
          setColumns((prev) => {
            const next = prev.map((c) =>
              c.id === updated.id ? { ...c, ...updated } : c
            );
            if (boardHasManualColumns(next)) {
              fetchColumnTickets(buildUnassignedColumn(), 1);
            }
            return next;
          });
          fetchColumnTickets(updated, 1);
          toast.success("Coluna atualizada!");
        } else {
          // Create
          const created = await createColumnApi({
            boardId: currentBoardId,
            name: colData.name,
            type: colData.type,
            color: colData.color,
            icon: colData.icon,
            filterJson: colData.filterJson,
            settingsJson: colData.settingsJson,
          });
          setColumns((prev) => {
            const next = upsertColumn(prev, created);
            if (boardHasManualColumns(next)) {
              fetchColumnTickets(buildUnassignedColumn(), 1);
            }
            return next;
          });
          fetchColumnTickets(created, 1);
          toast.success("Coluna criada!");
        }
      } catch (err) {
        toast.error("Erro ao salvar coluna");
      }
    },
    [currentBoardId, fetchColumnTickets, upsertColumn]
  );

  // ── Inline rename (double-click no título) ──
  const handleColumnRename = useCallback(
    async (columnId, newName) => {
      // Optimistic update
      setColumns((prev) =>
        prev.map((c) => (c.id === columnId ? { ...c, name: newName } : c))
      );
      try {
        await updateColumnApi(columnId, { name: newName });
      } catch (err) {
        console.error("[KanbanEnterprise] rename error:", err);
        toast.error("Erro ao renomear coluna");
        // Revert: refetch board
        if (currentBoardId) {
          const board = await showBoard(currentBoardId);
          setColumns(
            (board.columns || []).sort((a, b) => a.position - b.position)
          );
        }
      }
    },
    [currentBoardId]
  );

  const handleDeleteColumn = useCallback(
    async (colId) => {
      try {
        await deleteColumnApi(colId);
        setColumns((prev) => prev.filter((c) => c.id !== colId));
        setColumnTickets((prev) => {
          const next = { ...prev };
          delete next[colId];
          return next;
        });
        toast.success("Coluna excluida!");
      } catch (err) {
        toast.error("Erro ao excluir coluna");
      }
    },
    []
  );

  // ── Quick Actions from card menu ──
  const handleQuickAction = useCallback(
    async (actionType, ticketId, params = {}) => {
      try {
        switch (actionType) {
          case "priority": {
            // Direct update — no selector needed
            await quickUpdateTicket(ticketId, { priority: params.priority });
            // Optimistic: update ticket in local state
            setColumnTickets((prev) => {
              const next = { ...prev };
              for (const colId of Object.keys(next)) {
                next[colId] = (next[colId] || []).map((t) =>
                  t.id === ticketId ? { ...t, priority: params.priority } : t
                );
              }
              return next;
            });
            toast.success("Prioridade atualizada!");
            break;
          }
          case "assignee":
            setSelectorDialog({ type: "assignee", ticketId });
            break;
          case "queue":
            setSelectorDialog({ type: "queue", ticketId });
            break;
          case "connection":
            setSelectorDialog({ type: "connection", ticketId });
            break;
          case "history":
            setAuditTicketId(ticketId);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error("[KanbanEnterprise] quick action error:", err);
        toast.error("Erro na ação rápida");
      }
    },
    []
  );

  const handleSelectorConfirm = useCallback(
    async (selectedValue) => {
      const { type, ticketId } = selectorDialog;
      if (!type || !ticketId || selectedValue == null) {
        setSelectorDialog({ type: null, ticketId: null });
        return;
      }

      try {
        const payload = {};
        let fieldName = "";
        if (type === "assignee") {
          payload.assigneeId = selectedValue;
          fieldName = "userId"; // local state field is userId
        } else if (type === "queue") {
          payload.queueId = selectedValue;
          fieldName = "queueId";
        } else if (type === "connection") {
          payload.whatsappId = selectedValue;
          fieldName = "whatsappId";
        }

        await quickUpdateTicket(ticketId, payload);

        // Optimistic update
        setColumnTickets((prev) => {
          const next = { ...prev };
          for (const colId of Object.keys(next)) {
            next[colId] = (next[colId] || []).map((t) =>
              t.id === ticketId ? { ...t, [fieldName]: selectedValue } : t
            );
          }
          return next;
        });

        const labels = { assignee: "Vendedor", queue: "Fila", connection: "Conexão" };
        toast.success(`${labels[type]} atualizado(a)!`);
      } catch (err) {
        console.error("[KanbanEnterprise] selector confirm error:", err);
        toast.error("Erro ao atualizar ticket");
      } finally {
        setSelectorDialog({ type: null, ticketId: null });
      }
    },
    [selectorDialog]
  );

  const handleSelectorClose = useCallback(() => {
    setSelectorDialog({ type: null, ticketId: null });
  }, []);

  const handleRefresh = useCallback(() => {
    if (!currentBoardId) return;
    columns.forEach((col) => fetchColumnTickets(col, 1));
    if (boardHasManualColumns(columns)) {
      fetchColumnTickets(buildUnassignedColumn(), 1);
    }
  }, [columns, fetchColumnTickets, currentBoardId]);

  const inboxColumn = useMemo(
    () => (boardHasManualColumns(columns) ? buildUnassignedColumn() : null),
    [columns]
  );

  // ── Render ──
  return (
    <MainContainer>
      <MainHeader>
        <Title>Kanban Enterprise</Title>
        <MainHeaderButtonsWrapper>
          <BoardSelector
            boards={boards}
            currentBoardId={currentBoardId}
            onSelectBoard={handleSelectBoard}
            onCreateBoard={handleCreateBoard}
            onRenameBoard={handleRenameBoard}
            onDeleteBoard={handleDeleteBoard}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Dashboard Metrics (admin only) */}
        {user?.profile === "admin" && (
          <DashboardMetrics
            columns={columns}
            columnTickets={columnTickets}
            columnCounts={columnCounts}
          />
        )}

        {/* Board Filters */}
        <BoardFilters
          filters={boardFilters}
          onFilterChange={setBoardFilters}
          companyId={companyId}
        />

        {/* Toolbar */}
        <div className={classes.toolbar}>
          <Tooltip title="Adicionar coluna">
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleNewColumn}
            >
              Coluna
            </Button>
          </Tooltip>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <ViewModeToggle
            viewMode={viewMode}
            cardStyle={cardStyle}
            onChange={setViewMode}
            onCardStyleChange={setCardStyle}
          />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginLeft: "auto", fontSize: "0.78rem" }}
          >
            {columns.length} colunas
          </Typography>
        </div>

        {/* Board */}
        <div className={classes.boardArea}>
          {loading ? (
            <div className={classes.loadingCenter}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="textSecondary">
                Carregando...
              </Typography>
            </div>
          ) : (
            <KanbanEnterpriseBoard
              inboxColumn={inboxColumn}
              columns={columns}
              columnTickets={filteredColumnTickets}
              columnCounts={filteredColumnCounts}
              columnHasMore={columnHasMore}
              columnLoading={columnLoading}
              onLoadMore={handleLoadMore}
              onDragEnd={handleDragEnd}
              onEditColumn={handleEditColumn}
              onColumnRename={handleColumnRename}
              onQuickAction={handleQuickAction}
              isAdmin={user?.profile === "admin"}
              viewMode={viewMode}
              cardStyle={cardStyle}
            />
          )}
        </div>
      </Paper>

      {/* Column Editor Dialog */}
      {editorOpen && (
        <ColumnEditor
          open={editorOpen}
          column={editingColumn}
          columns={columns}
          onClose={() => {
            setEditorOpen(false);
            setEditingColumn(null);
          }}
          onSave={handleSaveColumn}
          onDelete={handleDeleteColumn}
        />
      )}

      {/* Quick Action Selector Dialogs */}
      <UserSelectionDialog
        open={selectorDialog.type === "assignee"}
        onClose={handleSelectorClose}
        onSelect={handleSelectorConfirm}
        title="Trocar Vendedor"
        companyId={companyId}
      />
      <QueueSelectionDialog
        open={selectorDialog.type === "queue"}
        onClose={handleSelectorClose}
        onSelect={handleSelectorConfirm}
        companyId={companyId}
      />
      <ConnectionSelectionDialog
        open={selectorDialog.type === "connection"}
        onClose={handleSelectorClose}
        onSelect={handleSelectorConfirm}
        companyId={companyId}
      />
      <TicketAuditDialog
        open={Boolean(auditTicketId)}
        ticketId={auditTicketId}
        onClose={() => setAuditTicketId(null)}
      />
    </MainContainer>
  );
};

export default KanbanEnterprise;
